import React, { useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useAtomValue, useSetAtom } from 'jotai'
import styled from 'styled-components'

import * as lib from '../../lib'
import * as state from '../../states'
import { TRoleAccess, TRoleExtra } from '../../types'
import { RoleAccessFields } from './RoleAccessFields'
import { CheckboxBox, CheckboxBoxes } from '../revenueAgency'
import { ALL_LANG_KEY } from '../../types/lang'
import { useTranslation } from 'react-i18next'
// import { FilterSelectMultiple } from '../filters'

type IRoleCreateModalProps = {
	show: boolean;
	onClose: () => void;
	onSave: () => void;
}

const AccessRule = styled.div`
    padding: 0px 12px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 6px;
`

const MyControl = styled(Form.Control)`
    display: inline-flex;
    height: unset;
    padding: 2px 4px !important;
    width: 16em;
`

const MyCheck = styled(Form.Check)`
    display: inline-flex;
`

export const RoleCreateModal = (
	props: IRoleCreateModalProps
) => {
	const { t } = useTranslation()
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const { show, onClose, onSave } = props

	const [code, setCode] = useState<string>('')
	const [name, setName] = useState<string>('')
	const [isSystem, setIsSystem] = useState<boolean>(false)
	const [rules, setRules] = useState<Array<TRoleAccess>>([])
	const [extra, setExtra] = useState<Partial<TRoleExtra>>(() => {
		return { inGroup: null, isBuyer: false }
	})
	const [teamReport, setTeamReport] = useState<string>('')
	const [notes, setNotes] = useState<string>('')

	if (!show) {
		return null
	}

	const onCreate = async () => {
		const uri: string = `role`
		const method: string = 'POST'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(access)
		const body: string = JSON.stringify({
			code,
			name,
			isSystem,
			access: rules,
			extra,
			teamReport,
			notes
		})
		const options: lib.FetchOptions = { body, method, params, headers }

		try {
			setFetchIsLoading(state.turnOnLoader)
			await lib.fetching<void>(uri, options)
			onSave()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const modalTitle = t(ALL_LANG_KEY.role_crud_modal.title_create)

	return <>
		<Modal show={show}
		       onHide={onClose}
		       scrollable={true}
		>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body className={'scroll'}>
				<Form>
					<Form.Group>
						<Form.Label className="text-dark" htmlFor={'input-code'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_code_label)}`}</Form.Label>
						<Form.Control
							id={'input-code'}
							placeholder={`${t(ALL_LANG_KEY.role_crud_modal.field_code_placeholder)}`}
							type="text"
							value={code}
							onChange={(e) => setCode(e.target.value)}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-name'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_name_label)}`}</Form.Label>
						<Form.Control
							id={'input-name'}
							placeholder={`${t(ALL_LANG_KEY.role_crud_modal.field_name_placeholder)}`}
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>

						<CheckboxBoxes>
							<CheckboxBox>
								<Form.Label className="text-dark" htmlFor={'input-is-system'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_is_system_label)}`}</Form.Label>
								<Form.Check
									checked={isSystem ?? false}
									id={'input-is-system'}
									type="checkbox"
									onChange={(e) => setIsSystem(e.target.checked)}
								/>
							</CheckboxBox>

							<CheckboxBox>
								<Form.Label className="text-dark" htmlFor={'input-extra-is-buyer'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_is_buyer_label)}`}</Form.Label>
								<Form.Check
									checked={extra?.isBuyer ?? false}
									id={'input-extra-is-buyer'}
									type="checkbox"
									onChange={(e) => setExtra({ ...extra, isBuyer: e.target.checked })}
								/>
							</CheckboxBox>
						</CheckboxBoxes>

						<Form.Label className="text-dark" htmlFor={'input-extra-in-group'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_in_group_label)}`}</Form.Label>
						<Form.Control
							id={'input-extra-in-group'}
							placeholder={`${t(ALL_LANG_KEY.role_crud_modal.field_in_group_placeholder)}`}
							type="text"
							value={extra?.inGroup ?? ''}
							onChange={(e) => setExtra({ ...extra, inGroup: e.target.value || null })}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-extra-is-team'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_is_team_label)}`}</Form.Label>
						<Form.Control
							id={'input-extra-is-team'}
							placeholder={`${t(ALL_LANG_KEY.role_crud_modal.field_is_team_placeholder)}`}
							type="text"
							value={extra?.isTeam ?? ''}
							onChange={(e) => setExtra({ ...extra, isTeam: e.target.value || null })}
						/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-team-report'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_team_report_label)}`}</Form.Label>
						<Form.Control
							id={'input-team-report'}
							placeholder={`${t(ALL_LANG_KEY.role_crud_modal.field_team_report_placeholder)}`}
							type="text"
							value={teamReport}
							onChange={(e) => setTeamReport(e.target.value)}
						/>
						<br/>
						<br/>

						{/* <FilterSelectMultiple
							placeholder="Is team"
							value={(extra?.isTeam ?? '').split(',')}
							options={(extra?.isTeam ?? '').split(',').map(_ => ({ value: _, label: <>{_}</> }))}
							setValue={(values) => setExtra({ ...extra, isTeam: values?.length ? values.join((',')) : null })}
						/>
						<br /> */}

						<Form.Label className="text-dark">{`${t(ALL_LANG_KEY.role_access_rule.title_label_access)}`}</Form.Label>
						<RoleAccessFields rules={rules} setRules={setRules}/>
						<br/>

						<Form.Label className="text-dark" htmlFor={'input-notes'}>{`${t(ALL_LANG_KEY.role_crud_modal.field_notes_label)}`}</Form.Label>
						<Form.Control
							id={'input-notes'}
							placeholder={`${t(ALL_LANG_KEY.role_crud_modal.field_notes_placeholder)}`}
							type="text"
							multiple={true}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</Form.Group>
				</Form>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary" onClick={onCreate}>{`${t(ALL_LANG_KEY.role_crud_modal.btn_create)}`}</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.role_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>
	</>
}
